import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { replacementAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  ArrowsRightLeftIcon,
  ComputerDesktopIcon,
  UserIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ArrowLongRightIcon
} from '@heroicons/react/24/outline';

const ReplacementAlgorithm = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [priorities, setPriorities] = useState({});
  const [stats, setStats] = useState({ totalExpiring: 0, totalSuggestions: 0, oldProcessorCount: 0 });
  const [loading, setLoading] = useState(true);
  const [showPriorities, setShowPriorities] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [suggestionsRes, prioritiesRes] = await Promise.all([
        replacementAPI.getSuggestions(),
        replacementAPI.getPriorities()
      ]);
      setSuggestions(suggestionsRes.data.suggestions || []);
      setStats(suggestionsRes.data.stats || {});
      setPriorities(prioritiesRes.data || {});
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getLifespanBadge = (remaining) => {
    if (remaining === null || remaining === undefined) return null;
    if (remaining === 0) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Ömür Doldu</span>;
    }
    if (remaining <= 6) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">{remaining} ay kaldı</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">{remaining} ay kaldı</span>;
  };

  const getScoreBadge = (score) => {
    let color = 'gray';
    if (score >= 40) color = 'green';
    else if (score >= 25) color = 'blue';
    else if (score >= 15) color = 'yellow';
    else color = 'red';

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${color}-100 text-${color}-800`}>
        Skor: {score}
      </span>
    );
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      1: 'Çok Yüksek',
      2: 'Yüksek',
      3: 'Orta-Yüksek',
      4: 'Orta',
      5: 'Orta-Düşük',
      6: 'Düşük',
      7: 'Çok Düşük',
      8: 'En Düşük'
    };
    return labels[priority] || 'Bilinmiyor';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ArrowsRightLeftIcon className="h-8 w-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Değişim Algoritması</h1>
            <p className="text-gray-500">Cihaz değişim önerileri ve optimizasyon</p>
          </div>
        </div>
        <button
          onClick={() => setShowPriorities(!showPriorities)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          <InformationCircleIcon className="h-5 w-5" />
          Öncelik Tablosu
        </button>
      </div>

      {/* Old Processor Warning Banner */}
      {stats.oldProcessorCount > 0 && (
        <div className="mb-6 bg-orange-100 border-l-4 border-orange-500 p-4 rounded-r-lg">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-6 w-6 text-orange-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-orange-800">
                Eski Islemci Uyarisi
              </h3>
              <p className="text-orange-700">
                <strong>{stats.oldProcessorCount}</strong> adet cihaz 6. nesil veya daha eski islemciye sahip.
                Bu cihazlar performans ve guvenlik acisindan degistirilmeli.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Ömrü Dolmaya Yakın</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalExpiring}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <ArrowsRightLeftIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Değişim Önerisi</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalSuggestions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100">
              <ComputerDesktopIcon className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Eski Islemcili</p>
              <p className="text-2xl font-bold text-gray-900">{stats.oldProcessorCount || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Optimizasyon Potansiyeli</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalSuggestions > 0 ? 'Var' : 'Yok'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Priority Table Modal */}
      {showPriorities && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Departman Öncelik Sırası</h3>
          <p className="text-sm text-gray-500 mb-4">
            Düşük numaralı departmanlar daha yüksek önceliklidir ve daha iyi cihazlara ihtiyaç duyar.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(priorities)
              .sort((a, b) => a[1] - b[1])
              .map(([dept, priority]) => (
                <div
                  key={dept}
                  className={`p-3 rounded-lg border ${
                    priority <= 2 ? 'border-green-300 bg-green-50' :
                    priority <= 4 ? 'border-blue-300 bg-blue-50' :
                    priority <= 6 ? 'border-yellow-300 bg-yellow-50' :
                    'border-gray-300 bg-gray-50'
                  }`}
                >
                  <div className="font-medium">{dept}</div>
                  <div className="text-sm text-gray-500">Öncelik: {priority}</div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Suggestions */}
      {suggestions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Değişim Önerisi Yok</h3>
          <p className="text-gray-500">
            Şu anda ömrü dolmaya yakın ve değiştirilmesi gereken cihaz bulunmuyor.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {suggestions.map((suggestion, index) => (
            <div key={index} className="bg-white rounded-lg shadow overflow-hidden">
              {/* Suggestion Header */}
              <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <ArrowsRightLeftIcon className="h-5 w-5" />
                  Değişim Önerisi #{index + 1}
                </h3>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
                  {/* Source Device */}
                  <div className="bg-red-50 rounded-lg p-4 border-2 border-red-200">
                    <div className="flex items-center gap-2 mb-3">
                      <ComputerDesktopIcon className="h-6 w-6 text-red-600" />
                      <span className="font-semibold text-red-800">Değiştirilecek Cihaz</span>
                    </div>
                    <div className="space-y-2">
                      <Link
                        to={`/devices/${suggestion.expiringDevice.device.id}`}
                        className="text-lg font-medium text-blue-600 hover:underline"
                      >
                        {suggestion.expiringDevice.device.name}
                      </Link>
                      <p className="text-sm text-gray-600">
                        {suggestion.expiringDevice.device.brand} {suggestion.expiringDevice.device.model}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {suggestion.expiringDevice.isOldProcessor && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                            {suggestion.expiringDevice.processorGeneration}. Nesil Islemci
                          </span>
                        )}
                        {getLifespanBadge(suggestion.expiringDevice.remainingLifespan)}
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          Skor: {suggestion.expiringDevice.performanceScore}
                        </span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-red-200">
                        <div className="flex items-center gap-2">
                          <UserIcon className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">
                            {suggestion.expiringDevice.device.assignedUser?.fullName}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 ml-6">
                          {suggestion.expiringDevice.device.assignedUser?.department}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex justify-center">
                    <div className="hidden lg:flex flex-col items-center">
                      <ArrowLongRightIcon className="h-12 w-12 text-purple-500" />
                      <span className="text-sm text-gray-500 mt-1">verilebilir</span>
                    </div>
                    <div className="lg:hidden">
                      <div className="w-full h-0.5 bg-purple-300 my-4"></div>
                    </div>
                  </div>

                  {/* Target Users */}
                  <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
                    <div className="flex items-center gap-2 mb-3">
                      <UserIcon className="h-6 w-6 text-green-600" />
                      <span className="font-semibold text-green-800">Önerilen Hedefler</span>
                    </div>
                    <div className="space-y-3">
                      {suggestion.suggestedTargets.map((target, tIndex) => (
                        <div
                          key={tIndex}
                          className={`p-3 rounded-lg ${
                            tIndex === 0 ? 'bg-green-100 border border-green-300' : 'bg-white border border-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">{target.user.fullName}</div>
                              <div className="text-xs text-gray-500">{target.user.department}</div>
                            </div>
                            {tIndex === 0 && (
                              <span className="px-2 py-1 text-xs bg-green-600 text-white rounded-full">
                                En Uygun
                              </span>
                            )}
                          </div>
                          {target.device && (
                            <div className="mt-2 text-xs text-gray-500">
                              Mevcut: {target.device.name} (Skor: {target.device.performanceScore})
                            </div>
                          )}
                          {!target.device && (
                            <div className="mt-2 text-xs text-orange-600">
                              Zimmetli cihazı yok
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Reason */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Öneri Sebebi:</h4>
                  <p className="text-sm text-gray-600">{suggestion.reason}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Algorithm Explanation */}
      <div className="mt-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
        <h3 className="text-lg font-semibold text-blue-800 mb-3">Algoritma Nasıl Çalışır?</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700">
          <div>
            <h4 className="font-medium mb-2">Performans Skoru Hesaplama:</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>İşlemci nesli (max 20 puan)</li>
              <li>RAM miktarı (max 15 puan)</li>
              <li>Depolama tipi ve boyutu (max 10 puan)</li>
              <li>Kalan ömür (max 15 puan)</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">Eslestirme Kriterleri:</h4>
            <ul className="list-disc list-inside space-y-1">
              <li><strong className="text-orange-700">6. nesil veya daha eski islemcili cihazlar oncelikli degisim listesine alinir</strong></li>
              <li>Omru 12 ay veya daha az kalan cihazlar degerlendirilir</li>
              <li>Yuksek oncelikli departmanlardan dusuk oncelikli departmanlara aktarim onerilir</li>
              <li>Hedef kullanicinin mevcut cihazi daha dusuk performansli olmalidir</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReplacementAlgorithm;
